import { useId, type ReactElement } from "react";
import type { DateTimeFormatter } from "../../settings/format-date-time.js";

/**
 * The ONE way the admin console discloses the zone its timestamps are in
 * (PB-5409).
 *
 * Every absolute time in the console is formatted in the reader's EFFECTIVE
 * zone — their saved Region preference, or the browser's detected zone until
 * they save one (`useDateTimeFormatter`). Audit said which since PB-5216; the
 * other sixteen surfaces printed a bare "Sep 22, 2026, 6:02 AM", which is a
 * silently different fact for a reader whose own clock is two and a half hours
 * away. Disclosure is what makes the digits readable, and naming the SOURCE is
 * what tells a reader whether the zone is theirs to change.
 *
 * It is rendered once per visual REGION — a table's timestamp header, a card's
 * matching label, the caption over a group of timestamps — never once per row,
 * which would be noise rather than information.
 *
 * Admin-local on purpose: the persisted-versus-detected rule is a shell Region
 * concern, not yet a cross-product design-system one.
 */
export interface AdminTimeZoneLabelProps {
  /**
   * The visible text the zone qualifies — usually the field's own label
   * ("When", "Last active", "Time"), and the formatted timestamp itself where
   * a value stands alone with no label of its own to hang the zone on.
   */
  readonly label: string;
  readonly formatter: DateTimeFormatter;
  /**
   * `stacked` drops the zone onto its own line at a smaller size, for a table
   * header in a column too narrow to carry both on one. `inline` keeps the
   * sentence on one line, for a card label, a caption or a dialog.
   */
  readonly variant?: "stacked" | "inline";
}

/** The zone as it reads beside the label: "GMT+5:30", or "GMT+5:30, detected"
    while it is only the browser's guess. */
function zoneText(formatter: DateTimeFormatter): string {
  return formatter.persisted
    ? formatter.timeZoneLabel
    : `${formatter.timeZoneLabel}, detected`;
}

/** The full story, for a tooltip and an accessible description: the IANA zone
    the short label stands for, and where it came from. The short label is
    ambiguous by design — several zones render "GMT+5:30" — and the reader can
    only act on the difference if they know which one is theirs. */
function zoneTitle(formatter: DateTimeFormatter): string {
  return formatter.persisted
    ? `Times in ${formatter.timeZone}, your Region preference.`
    : `Times in ${formatter.timeZone}, detected from this browser. Save a Region preference to choose the zone.`;
}

export function AdminTimeZoneLabel({
  label,
  formatter,
  variant = "stacked",
}: AdminTimeZoneLabelProps): ReactElement {
  const describedBy = useId();
  const title = zoneTitle(formatter);
  /* The WRAPPER is what decides the layout, not only the zone inside it. While
     this was `block` in both variants, `inline` was inline in name only: every
     sentence that names an instant — "The invite stays valid until X.", "Last
     changed X", "Last synced X" — took a line break in the middle of itself,
     because a block element in running text breaks the line whatever its
     children do. The first test for this compared the two variants' text, which
     is identical by design, so it could never have caught it. */
  const wrapperClass = variant === "stacked" ? "block" : "inline";
  /* The zone is rendered at normal weight and case in both layouts. Admin
     table headers are uppercase with tracking, and "(GMT+5:30)" in that
     treatment reads as a second heading rather than as an aside about the
     column beside it. */
  const zoneClass =
    variant === "stacked"
      ? "block text-dense-2 font-normal normal-case tracking-normal text-pb-text-muted"
      : "font-normal normal-case tracking-normal text-pb-text-muted";
  return (
    <>
      <span
        title={title}
        aria-describedby={describedBy}
        className={wrapperClass}
      >
        {/* The space is load-bearing. In the stacked layout the zone renders on
            its own line, but the accessible name and the visible text are still
            one sentence — "Last active (GMT+5:30)". Without it both read
            "Last active(GMT+5:30)". */}
        {label} <span className={zoneClass}>({zoneText(formatter)})</span>
      </span>
      {/* `title` alone reaches a mouse and nothing else: the span is not
          focusable, so a keyboard never surfaces it, and screen-reader support
          for `title` is inconsistent. The same sentence is put where assistive
          technology reliably reads it, as a DESCRIPTION rather than part of the
          name — "(GMT+5:30)" is ambiguous on its own, and which zone it stands
          for is exactly what a reader needs to be able to reach.

          `hidden` rather than a visually-hidden class on purpose: a referenced
          hidden element still resolves for `aria-describedby`, and staying out
          of the accessibility tree keeps it out of the NAME of the column
          header or card label this sits in. */}
      <span id={describedBy} hidden>
        {title}
      </span>
    </>
  );
}
